<template>
    <div class="main_div">
        <div style="height: auto">
            <Loading v-show="isLoading" />
            <n-space>
                <NCard style="margin-top: 20px; margin-left: 80px; border: 0px; min-width: 800px">
                    <n-space item-style="display: flex;" align="center">
                        <div style="font-size: 20px; width: 250px; font-weight: bold">홍보 관리 > 홍보 등록</div>
                    </n-space>
                    <div class="content_delimiter_line"></div>
                </NCard>
            </n-space>
            <n-space style="width: 1400px; min-width: 1400px; height: auto">
                <NCard style="margin-top: 20px; margin-left: 100px; width: 1400px; min-width: 1400px; height: 100%">
                    <n-space item-style="display: flex;">
                        <div style="font-size: 20px; width: 150px; font-weight: bold">제목</div>
                        <NInput
                            type="text"
                            placeholder=""
                            v-model:value="contentTitle"
                            style="width: 1000px; height: 30px; text-align: left"
                        />
                    </n-space>
                    <div class="add_content_delimiter_line"></div>

                    <n-space>
                        <div style="font-size: 20px; width: 150px; font-weight: bold">내용</div>

                        <quillEditor
                            v-if="editorReady"
                            v-model:value="content"
                            :options="editorOption"
                            theme="snow"
                            @blur="onBlur"
                            @focus="onFocus"
                            @change="onChange"
                            @ready="onReady"
                            style="width: auto; height: 450px"
                        />
                    </n-space>

                    <div class="add_content_delimiter_line"></div>

                    <n-space item-style="display: flex; align-items: center;">
                        <div style="font-size: 20px; width: 150px; font-weight: bold">첨부파일</div>
                        <NUpload action="#" :default-upload="false" v-model:file-list="fileList" @change="handleUploadChange" :max="1">
                            <NButton>파일 선택</NButton>
                        </NUpload>
                        <div v-if="uploadedFileName" style="margin-left: 10px">선택된 파일: {{ uploadedFileName }}</div>
                    </n-space>
                    <n-space style="margin-top: 50px" justify="center">
                        <NButton
                            dashed
                            style="width: 250px; height: 30px; border: none"
                            type="warning"
                            v-on:click="hide"
                        >
                            취소
                        </NButton>

                        <NButton
                            dashed
                            style="width: 250px; height: 30px; border: none"
                            type="success"
                            v-on:click="checkAddPress"
                        >
                            {{ submitButtonText }}
                        </NButton>
                    </n-space>
                </NCard>
            </n-space>
        </div>
        <NModal
            v-model:show="isAlert"
            :mask-closable="false"
            :content="alertMessage"
            :type="alertType"
            preset="dialog"
            title="Dialog"
            positive-text="확인"
            @positive-click="onPositiveClick"
        >
        </NModal>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from "vue";
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import { quillEditor, Quill } from "vue3-quill";
import { getCallable } from "@/config/env";
import {
    NRadio,
    NCheckbox,
    NSpace,
    NCard,
    NSelect,
    NButton,
    NInput,
    NModal,
    NRadioGroup,
    NUpload,
    useMessage,
    useNotification,
    useDialog,
} from "naive-ui";
import Loading from "@/components/Loading.vue";
import * as common from "@/assets/common/common";
import * as constant from "@/constants/constant";

import { Board } from "@/model/board";
import ImageResize from "quill-image-resize-vue";
Quill.register("modules/imageResize", ImageResize);

const addPressCallable = getCallable("addBoard");
const uploadAttachmentCallable = getCallable("uploadAttachment");

const props = defineProps<{
    isAddPress: boolean;
    selectedItem?: Board;
}>();
// 에디터 콘텐츠
const content = ref<string>("");

const emit = defineEmits(["hide"]);
const message = useMessage();
const notification = useNotification();
const dialog = useDialog();
const isLoading = ref<boolean>(false);
const error = ref<string | null>(null);
const submitButtonText = ref<string | null>(null);
const contentTitle = ref<string | null>(null);
const isAlert = ref<boolean>(false);
const alertMessage = ref<string | null>(null);
const alertType = ref<string | null>(null);
const editorReady = ref(false); // 에디터 준비 상태를 위한 ref
const uploadedFileId = ref<string | null>(null); // 업로드된 파일 ID 저장
const uploadedFileName = ref<string | null>(null); // 업로드된 파일 이름 표시용
const fileList = ref<any[]>([]); // NUpload 컴포넌트의 실제 파일 리스트 바인딩용
const editorOption = ref({
    modules: {
        // toolbar: [
        //   ["bold", "italic", "underline", "strike"], // 굵게, 기울임, 밑줄, 취소선
        //   ["blockquote", "code-block"], // 인용구, 코드 블록
        //   [{ header: 1 }, { header: 2 }], // 제목 크기
        //   [{ list: "ordered" }, { list: "bullet" }], // 순서 있는 목록, 순서 없는 목록
        //   [{ script: "sub" }, { script: "super" }], // 아래 첨자, 위 첨자
        //   [{ indent: "-1" }, { indent: "+1" }], // 들여쓰기
        //   [{ direction: "rtl" }], // 글쓰기 방향
        //   [{ size: ["small", false, "large", "huge"] }], // 글자 크기
        //   [{ header: [1, 2, 3, 4, 5, 6, false] }],
        //   [{ color: [] }, { background: [] }], // 글자색, 배경색
        //   [{ font: [] }],
        //   [{ align: [] }],
        //   ["clean"], // 서식 지우기
        //   ["link", "image", "video"], // 링크, 이미지, 비디오
        // ],
        imageResize: true,
    },
    placeholder: "여기에 내용을 입력하세요...", // placeholder도 options 객체로 설정 가능
    theme: "snow", // 테마도 여기서 설정 가능
});
onMounted(() => {
    editorReady.value = true; // 컴포넌트가 마운트된 후 editorReady를 true로 설정
});
watch(
    () => props.isAddPress,
    (newIsAddPress: boolean) => {
        submitButtonText.value = "등록";
        clearEditor();
    }
);
function onBlur(quill: any) {
    // console.log("editor blur!", quill);
}
function onFocus(quill: any) {
    // console.log("editor focus!", quill);
}
function onChange(quill: any) {
    // console.log("editor change!", quill);
}
function onReady(quill: any) {
    // console.log("editor ready!", quill);
}
function clearEditor() {
    contentTitle.value = "";
    content.value = "";
    uploadedFileName.value = null;
    uploadedFileId.value = null;
    fileList.value = []; // 업로드된 파일 리스트 명시적 초기화
}

function onPositiveClick() {
    isAlert.value = false;
}

function checkAddPress() {
    if (!contentTitle.value) {
        // 제목이 없는 경우
        alertMessage.value = "제목을 입력해주세요.";
        alertType.value = "error";
        isAlert.value = true;
        return;
    }
    addPressItem();
}

async function addPressItem() {
    isLoading.value = true;

    try {
        const result = await addPressCallable({
            boardType: constant.boardType.PRESS,
            boardData: {
                title: contentTitle.value,
                author: "(주)COKO Lab.",
                content: content.value,
                attached_file_name: uploadedFileName.value,
                attached_file_drive_id: uploadedFileId.value, // 첨부파일 ID 추가
            },
        });
        if (common.evalFunctionResult(result)) {
            message.success("홍보 등록 성공!");
            hide();
        } else {
            notification.error({
                title: "에러",
                description: `홍보 등록 실패: ${common.getResultError(result) || "오류가 발생했습니다."}`,
                duration: 1500,
                closable: true,
            });
        }
    } catch (error) {
        notification.error({
            title: "에러",
            description: `홍보 등록 실패: ${error || "오류가 발생했습니다."}`,
            duration: 1500,
            closable: true,
        });
    } finally {
        isLoading.value = false;
    }
}
function hide() {
    emit("hide");
}

async function handleUploadChange(options: {
    file: { file: File | null; status: string };
    fileList: Array<{ file: File | null; status: string }>;
}) {
    const { file } = options;
    if (file.file && file.status !== "removed") {
        uploadedFileName.value = file.file.name; // 파일 이름 표시
        await handleFileUpload(file.file);
    } else if (file.status === "removed") {
        uploadedFileName.value = null;
        uploadedFileId.value = null; // 파일 제거 시 ID도 초기화
    }
}

async function handleFileUpload(file) {
    if (!file) return;
    isLoading.value = true; // 로딩 시작
    const reader = new FileReader();
    reader.readAsDataURL(file); // 파일을 Base64로 읽음
    reader.onload = async () => {
        const base64String = (reader.result as string).split(",")[1]; // 'data:mime/type;base64,' 부분 제거
        try {
            const result = await uploadAttachmentCallable({
                file: {
                    base64: base64String,
                    name: file.name,
                    type: file.type,
                },
            });
            if (common.evalFunctionResult(result)) {
                const fileId = common.getItemFromFunctionResult(result);
                uploadedFileId.value = fileId;
            } else {
                notification.error({
                    title: "업로드 실패",
                    description: common.getResultError(result) || "파일 업로드 중 오류 발생",
                    duration: 3000,
                });
            }
        } catch (error) {
            uploadedFileName.value = null; // 실패 시 파일 이름 표시 제거
            console.error("Error uploading file:", error);
            notification.error({
                title: "업로드 오류",
                description: error.message || "파일 업로드 중 심각한 오류 발생",
                duration: 3000,
            });
        } finally {
            isLoading.value = false; // 로딩 종료
        }
    };
    reader.onerror = (error) => {
        console.error("Error reading file:", error);
    };
}
</script>

<style scope>
.outer {
    position: relative;
}
.inner {
    position: absolute;
}
.add_content_delimiter_line {
    border: lightgrey 2px solid;
    width: 100%;
    margin-top: 15px;
    margin-bottom: 15px;
}
</style>
